import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import { count, utc } from "../format.js";
import {
  Blank,
  Button,
  ErrorNote,
  Panel,
  Pill,
  ScreenHeader,
  ShortId,
  Spinner,
  Table,
  Td,
  Th,
  inputClass,
} from "../ui.js";

/**
 * Plan subscriptions across every account: `GET /v1/admin/subscriptions` (viewer).
 *
 * READ ONLY. No control here changes a subscription; the status shown is the stored one, so
 * `grace` appears as itself (the customer view folds it into `suspended`). Pagination follows
 * the endpoint's keyset cursor forward; "Newest page" returns to the start.
 */

const PAGE_LIMIT = 50;
const STATUSES = ["active", "grace", "paused", "suspended", "cancelled"] as const;
const TONE: Record<string, "good" | "attention" | "bad" | "neutral"> = {
  active: "good",
  grace: "attention",
  paused: "neutral",
  suspended: "bad",
  cancelled: "neutral",
};

export function SubscriptionsScreen() {
  const [status, setStatus] = useState("");
  const [accountId, setAccountId] = useState("");
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const list = useResource(
    (signal) => {
      void signal;
      return adminReads.subscriptions({
        ...(status === "" ? {} : { status }),
        ...(accountId === "" ? {} : { accountId }),
        ...(cursor === undefined ? {} : { cursor }),
        limit: PAGE_LIMIT,
      });
    },
    [status, accountId, cursor],
  );
  const rows = list.data?.data ?? [];
  const next = list.data?.next_cursor ?? null;

  return (
    <>
      <ScreenHeader
        title="Subscriptions"
        lead="Every account's plan subscriptions, newest first. Status and account are filtered by the API. Read only."
      />

      <Panel>
        <div class="grid gap-2 sm:grid-cols-3">
          <input
            class={inputClass}
            aria-label="Account id"
            placeholder="account id"
            value={accountId}
            onInput={(event) => {
              setAccountId((event.currentTarget as HTMLInputElement).value.trim());
              setCursors([]);
            }}
          />
          <select
            class={inputClass}
            aria-label="Status"
            value={status}
            onChange={(event) => {
              setStatus((event.currentTarget as HTMLSelectElement).value);
              setCursors([]);
            }}
          >
            <option value="">every status</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </Panel>

      <div class="mt-3">
        <Panel
          title={`Subscriptions (${rows.length}${next === null ? "" : "+"})`}
          subtitle={`Newest first, ${PAGE_LIMIT} per page. Times are UTC, like every other screen.`}
        >
          {list.loading ? (
            <Spinner />
          ) : list.error !== null ? (
            <ErrorNote error={list.error} onRetry={() => list.reload()} />
          ) : (
            <Table
              rows={rows.length}
              empty="No subscription matches these filters. Clear the account id or pick every status."
              head={
                <tr>
                  <Th>Subscription</Th>
                  <Th>Account</Th>
                  <Th>Plan</Th>
                  <Th>Status</Th>
                  <Th numeric>Reserve / delegated in</Th>
                  <Th>Next billing</Th>
                  <Th>Created</Th>
                </tr>
              }
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <Td mono>
                    <ShortId value={row.id} />
                  </Td>
                  <Td mono>
                    <ShortId value={row.account_id} />
                  </Td>
                  <Td>{row.plan_name ?? row.plan ?? <Blank title="A legacy watcher has no plan" />}</Td>
                  <Td>
                    <Pill value={row.status} tone={TONE[row.status] ?? "neutral"} />
                  </Td>
                  <Td numeric>
                    {row.reserve_energy === null ? "—" : count(row.reserve_energy)} / {count(row.delegated_in)}
                  </Td>
                  <Td mono>{utc(row.next_billing_at)}</Td>
                  <Td mono>{utc(row.created_at)}</Td>
                </tr>
              ))}
            </Table>
          )}
          <div class="mt-3 flex gap-2">
            {cursors.length === 0 ? null : (
              <Button tone="ghost" onClick={() => setCursors([])}>
                Newest page
              </Button>
            )}
            {next === null ? null : (
              <Button tone="ghost" onClick={() => setCursors([...cursors, next])}>
                Older
              </Button>
            )}
          </div>
        </Panel>
      </div>
    </>
  );
}
